import { Coin, CurrencyDollar, CurrencyEur, Wallet } from '@phosphor-icons/react'
import type { Currency } from '@/types/database.types'

export const CURRENCY_THEMES: Record<
  Currency,
  {
    accent: string // Ana vurgu rengi (border, text)
    accentBg: string // Rozet arka planı
    accentBorder: string // Kart kenar vurgusu
    badgeBg: string // Tur rozeti bg
    badgeText: string // Tur rozeti text
    progressBar: string // İlerleme çubuğu
    watermark: string // Filigran opacity
    label: string // Para birimi etiketi
    symbol: string // Kısaltma
    Icon: React.ElementType // İkon bileşeni
    contrastText?: 'white' | 'espresso'
    leatherGradient: string // Deri cüzdan gradyanı
    stitchingColor: string // Dikiş izi rengi
    motifClass: string // Para birimi motifi sınıfı
    cardTextColor?: string // Kart metin rengi (opsiyonel)
    cardSubTextColor?: string // Kart alt metin rengi (opsiyonel)
    progressBarBg?: string // Progress bar arka planı (opsiyonel)
    progressBarFill?: string // Progress bar dolgu rengi (opsiyonel)
  }
> = {
  GRAM_GOLD: {
    accent: '#D4AF37',
    accentBg: '#FFF9E6',
    accentBorder: '#f5ebd1',
    badgeBg: '#fcf5e3',
    badgeText: '#D4AF37',
    progressBar: '#D4AF37',
    watermark: 'rgba(212,175,55,0.06)',
    label: 'Gram (24 Ayar)',
    symbol: ' Gram Altın',
    Icon: Coin,
    contrastText: 'espresso',
    leatherGradient: 'linear-gradient(135deg, #E6C96C 0%, #DCB645 50%, #B8922C 100%)',
    stitchingColor: 'rgba(0, 0, 0, 0.1)',
    motifClass: 'motif-gold',
    cardTextColor: '#1A1A1A',
    cardSubTextColor: 'rgba(26, 26, 26, 0.5)',
    progressBarBg: 'rgba(0, 0, 0, 0.1)',
    progressBarFill: '#1A1A1A',
  },
  TRY: {
    accent: '#008585',
    accentBg: '#F0FDFD',
    accentBorder: '#CCFBF1',
    badgeBg: '#F0FDFA',
    badgeText: '#00A595',
    progressBar: '#00A595',
    watermark: 'rgba(0,165,149,0.06)',
    label: 'Türk Lirası',
    symbol: ' TL',
    Icon: Wallet,
    contrastText: 'white',
    leatherGradient: 'linear-gradient(135deg, #00A595 0%, #007A70 100%)', // Turkuaz
    stitchingColor: 'rgba(255, 255, 255, 0.3)',
    motifClass: 'motif-try',
  },
  USD: {
    accent: '#14532D', // Darker investor green (Green-900 equivalent) to match cards
    accentBg: '#DCFCE7',
    accentBorder: '#DCFCE7',
    badgeBg: '#F0FDF4',
    badgeText: '#16A34A',
    progressBar: '#22C55E',
    watermark: 'rgba(22,163,74,0.06)',
    label: 'Dolar',
    symbol: ' USD',
    Icon: CurrencyDollar,
    contrastText: 'white',
    leatherGradient: 'linear-gradient(135deg, #166534 0%, #14532D 100%)',
    stitchingColor: 'rgba(212, 175, 55, 0.4)',
    motifClass: 'motif-usd',
  },
  EUR: {
    accent: '#003399',
    accentBg: '#F0F4FF',
    accentBorder: '#E0F2FE',
    badgeBg: '#F0F9FF',
    badgeText: '#0EA5E9',
    progressBar: '#38BDF8',
    watermark: 'rgba(14,165,233,0.06)',
    label: 'Euro',
    symbol: ' EUR',
    Icon: CurrencyEur,
    contrastText: 'white',
    leatherGradient: 'linear-gradient(135deg, #1E3A8A 0%, #172554 100%)',
    stitchingColor: 'rgba(255, 255, 255, 0.3)',
    motifClass: 'motif-eur',
  },
  GRAM_SILVER: {
    accent: '#8E9196',
    accentBg: '#F3F4F6',
    accentBorder: '#E5E7EB',
    badgeBg: '#F9FAFB',
    badgeText: '#6B7280',
    progressBar: '#9CA3AF',
    watermark: 'rgba(156,163,175,0.06)',
    label: 'Gram Gümüş',
    symbol: ' Gram Gümüş',
    Icon: Coin,
    contrastText: 'espresso',
    leatherGradient: 'linear-gradient(135deg, #E5E7EB 0%, #D1D5DB 50%, #9CA3AF 100%)',
    stitchingColor: 'rgba(0, 0, 0, 0.1)',
    motifClass: 'motif-silver',
    cardTextColor: '#1A1A1A',
    cardSubTextColor: 'rgba(26, 26, 26, 0.5)',
    progressBarBg: 'rgba(0, 0, 0, 0.05)',
    progressBarFill: '#1A1A1A',
  },
} as any

/**
 * Bu bölüm veritabanındaki diğer altın tiplerini de merkezi sisteme dahil eder.
 * @note record key'leri database.types içindeki Currency enum'ı ile tam eşleşmelidir.
 */
export const EXTENDED_CURRENCY_THEMES = {
  ...CURRENCY_THEMES,
  GRAM_24_GOLD: { ...CURRENCY_THEMES.GRAM_GOLD, label: 'Gram (24 Ayar)', symbol: ' Gram Altın' },
  GRAM_22_GOLD: { ...CURRENCY_THEMES.GRAM_GOLD, label: 'Gram (22 Ayar)', symbol: ' Gram Altın' },
  QUARTER_GOLD: { ...CURRENCY_THEMES.GRAM_GOLD, label: 'Çeyrek Altın', symbol: ' Çeyrek Altın' },
  HALF_GOLD: { ...CURRENCY_THEMES.GRAM_GOLD, label: 'Yarım Altın', symbol: ' Yarım Altın' },
  FULL_GOLD: { ...CURRENCY_THEMES.GRAM_GOLD, label: 'Tam Altın', symbol: ' Tam Altın' },
} as Record<string, any>
